import { ComponentRef, forwardRef, Inject, Injectable, ViewContainerRef } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { STYLE_CONFIGURATION } from "../common/endpoints";
import { MatSelectionFieldComponent } from "../shared/component/mat-selection-field/mat-selection-field.component";
import { PeriodFieldSelectionStyle, PeriodFieldV2Component } from "../shared/component/period-field/period-field-v2.component";
import { ServitlyBadgeComponent } from "../style/badge/servitly-badge.component";
import { ServitlyButtonComponent } from "../style/button/servitly-button.component";
import { ServitlyIconButtonComponent } from "../style/icon-button/servitly-icon-button.component";
import { ServitlyToggleButtonComponent } from "../style/toggle-button/servitly-toggle-button.component";
import { ScriptHelper } from "../utility";
import { DateRangeName } from "./date-range.service";
import { HttpService } from "./http.service";

@Injectable()
export class StyleConfigurationService {

    private uiToolkit: UIToolkit;

    constructor(@Inject(forwardRef(() => HttpService)) private httpService: HttpService) { }

    load(vcRef: ViewContainerRef): void {
        firstValueFrom(this.httpService.get<StyleConfiguration>(STYLE_CONFIGURATION)).then(config => {
            this.uiToolkit = new UIToolkit(config);
            config.defaultFontHrefs?.forEach(src => ScriptHelper.appendLinkCss(src, vcRef.element.nativeElement));
        });
    }

    getUIToolkit(): UIToolkit {
        return this.uiToolkit;
    }
}

export interface StyleConfiguration {
    randomSelectionColors: string[];
    defaultFontHrefs: string[];
}

export class UIToolkit {
    private config: StyleConfiguration;
    private static RANDOM_COLOR_IDX = 0;

    constructor(config: StyleConfiguration) { this.config = config };

    getColor(token: string): string {
        return getComputedStyle(document.documentElement).getPropertyValue(this.toCssVariable(token)).trim();
    }

    getOnColor(token: string): string {
        return this.getColor(token + '-on');
    }

    assignColor(token: string, code?: string): string {
        if (!code) {
            code = this.getRandomColor();
        }
        document.documentElement.style.setProperty(this.toCssVariable(token), code);
        return code;
    }

    getRandomColor(): string {
        const randomColors = this.config.randomSelectionColors;
        const idx = UIToolkit.RANDOM_COLOR_IDX++ % randomColors.length;
        return randomColors[idx];
    }

    private toCssVariable(token: string): string {
        token = token.toLowerCase();
        return token.startsWith('--') ? token : `--${token}`;
    }

    getCategoricalColorPalette(): string[] {
        return this.config.randomSelectionColors;
    }

    createButton(container: Element, settings: ButtonSettings, context: any): ServitlyButtonComponent {
        if (container && context?.viewContainerRef) {
            const buttonComponent: ComponentRef<ServitlyButtonComponent> = context.viewContainerRef.createComponent(ServitlyButtonComponent);
            buttonComponent.instance.type = settings.type;
            buttonComponent.instance.label = settings.label;
            buttonComponent.instance.disabled = settings.disabled;
            buttonComponent.instance.cssClass = settings.cssClass;
            buttonComponent.instance.critical = settings.critical;
            if (settings?.onClick) {
                buttonComponent.instance.buttonClick.subscribe(() => settings.onClick());
            }
            const componentElement: Element = buttonComponent.location.nativeElement;
            container.appendChild(componentElement);
            return buttonComponent.instance;
        } else {
            console.error("Invalid element or missing context");
            return null;
        }
    }

    createIconButton(container: Element, settings: IconButtonSettings, context: any): ServitlyIconButtonComponent {
        if (container && context?.viewContainerRef) {
            const buttonComponent: ComponentRef<ServitlyIconButtonComponent> = context.viewContainerRef.createComponent(ServitlyIconButtonComponent);
            buttonComponent.instance.icon = settings.icon;
            buttonComponent.instance.tooltip = settings.tooltip;
            buttonComponent.instance.color = settings.color;
            buttonComponent.instance.disabled = settings.disabled;
            buttonComponent.instance.cssClass = settings.cssClass;
            if (settings?.onClick) {
                buttonComponent.instance.buttonClick.subscribe(() => settings.onClick());
            }
            const componentElement: Element = buttonComponent.location.nativeElement;
            container.appendChild(componentElement);
            return buttonComponent.instance;
        } else {
            console.error("Invalid element or missing context");
            return null;
        }
    }

    createToggleButton(container: Element, settings: ToggleButtonSettings, context: any): ServitlyToggleButtonComponent {
        if (container && context?.viewContainerRef) {
            const buttonComponent: ComponentRef<ServitlyToggleButtonComponent> = context.viewContainerRef.createComponent(ServitlyToggleButtonComponent);
            buttonComponent.instance.items = settings.items;
            buttonComponent.instance.initialValue = settings.initialValue;
            buttonComponent.instance.multiple = settings.multiple;
            buttonComponent.instance.disabled = settings.disabled;
            buttonComponent.instance.cssClass = settings.cssClass;
            if (settings?.onChange) {
                buttonComponent.instance.change.subscribe(value => settings.onChange(value));
            }
            const componentElement: Element = buttonComponent.location.nativeElement;
            container.appendChild(componentElement);
            return buttonComponent.instance;
        } else {
            console.error("Invalid element or missing context");
            return null;
        }
    }

    createBadge(container: Element, settings: BadgeSettings, context: any): ServitlyBadgeComponent {
        if (context?.viewContainerRef) {
            const badgeComponent: ComponentRef<ServitlyBadgeComponent> = context.viewContainerRef.createComponent(ServitlyBadgeComponent);
            badgeComponent.instance.label = settings.label;
            if (settings.showLeadingIcon == false) { // if explicit false set. Otherwise default is true
                badgeComponent.instance.showLeadingIcon = settings.showLeadingIcon;
            }
            badgeComponent.instance.colorToken = settings.colorToken;
            badgeComponent.instance.colorTokenPrefix = settings.colorTokenPrefix;
            badgeComponent.instance.color = settings.color;
            const componentElement: Element = badgeComponent.location.nativeElement;
            container?.appendChild(componentElement);
            return badgeComponent.instance;
        } else {
            console.error("Invalid element or missing context");
            return null;
        }
    }

    createPeriodPicker(element: Element, periodV2Settings: PeriodFieldV2Settings, context: any): PeriodFieldV2Component {
        if (element && context?.viewContainerRef) {
            let periodV2Component: ComponentRef<PeriodFieldV2Component> = context.viewContainerRef.createComponent(PeriodFieldV2Component);

            // all the "if"s preserve the default value instead of 'undefined'
            if (periodV2Settings.id) {
                periodV2Component.instance.id = periodV2Settings.id;
            }
            if (periodV2Settings.startVariable) {
                periodV2Component.instance.startVariable = periodV2Settings.startVariable;
            }
            if (periodV2Settings.endVariable) {
                periodV2Component.instance.endVariable = periodV2Settings.endVariable;
            }
            periodV2Component.instance.periods = periodV2Settings.periods;
            if (periodV2Settings.defaultPeriodValue) {
                periodV2Component.instance.defaultPeriodValue = periodV2Settings.defaultPeriodValue;
            }
            if (periodV2Settings.periodSelectionStyle) {
                periodV2Component.instance.periodSelectionStyle = periodV2Settings.periodSelectionStyle;
            }
            periodV2Component.instance.maxDaysBack = periodV2Settings.maxDaysBack;
            if (periodV2Settings.futureDatesSelectable) {
                periodV2Component.instance.futureDatesSelectable = periodV2Settings.futureDatesSelectable;
            }
            if (periodV2Settings.periodSelectionEnabled) {
                periodV2Component.instance.periodSelectionEnabled = periodV2Settings.periodSelectionEnabled;
            }
            if (periodV2Settings.onChange) {
                periodV2Component.instance.onChange = periodV2Settings.onChange;
            }
            if (periodV2Settings.preserveSelection) {
                periodV2Component.instance.preserveSelection = periodV2Settings.preserveSelection;
            }
            const componentElement: Element = periodV2Component.location.nativeElement;
            element.appendChild(componentElement);
            return periodV2Component.instance;
        } else {
            console.error("Invalid element or missing context");
            return null;
        }
    }

    createSelectionField(element: Element, settings: SelectionFieldSettings, context: any): MatSelectionFieldComponent {
        if (element && context?.viewContainerRef) {
            let matSelectionField: ComponentRef<MatSelectionFieldComponent> = context.viewContainerRef.createComponent(MatSelectionFieldComponent);
            matSelectionField.instance.label = settings.label;
            matSelectionField.instance.description = settings.description;
            matSelectionField.instance.placeholder = settings.placeholder;
            matSelectionField.instance.defaultValue = settings.defaultValue;
            matSelectionField.instance.values = settings.values;
            matSelectionField.instance.multiple = settings.multiple;
            matSelectionField.instance.mandatory = settings.mandatory;
            matSelectionField.instance.clearable = settings.clearable;
            matSelectionField.instance.callback = settings.callback;
            const componentElement: Element = matSelectionField.location.nativeElement;
            element.appendChild(componentElement);
            return matSelectionField.instance;
        } else {
            console.error("Invalid element or missing context");
            return null;
        }
    }
}

interface ButtonSettings {
    type: "FILLED" | "OUTLINED" | "STANDARD";
    label: string;
    disabled: boolean;
    cssClass: string;
    critical: boolean;
    onClick: () => {};
}

interface IconButtonSettings {
    icon: string;
    tooltip: string;
    color: string;
    disabled: boolean;
    cssClass: string;
    onClick: () => {};
}

interface ToggleButtonSettings {
    items: { label: string, value: any, icon?: string }[];
    initialValue: any;
    multiple: boolean;
    disabled: boolean;
    cssClass: string;
    onChange: (value: any) => {};
}

export interface BadgeSettings {
    label?: string;
    showLeadingIcon?: boolean;
    colorTokenPrefix?: string;
    colorToken?: string;
    color?: string;
}

export interface PeriodFieldV2Settings {
    id: string;
    startVariable: string;
    endVariable: string;
    periods: string[];
    defaultPeriodValue: DateRangeName;
    periodSelectionStyle: PeriodFieldSelectionStyle;
    maxDaysBack: number;
    futureDatesSelectable: boolean;
    periodSelectionEnabled: boolean;
    onChange: Function;
    preserveSelection: boolean;
}

export interface SelectionFieldSettings {
    label: string;
    description: string;
    placeholder: string;
    defaultValue: string | string[];
    values: { label: string, value: string }[];
    multiple: boolean;
    mandatory: boolean;
    clearable: boolean;
    callback: Function;
}